/// <reference path="./env.d.ts" />
// ============================================
// @gs/config — Variables d'environnement GS (Zod)
// ============================================
import { z } from 'zod';
// ─── Schéma serveur ───────────────────────────────
/** SQL user/password requis sauf si Managed Identity (`SQL_USE_MANAGED_IDENTITY=true`). */
const serverEnvSchema = z
    .object({
    // Azure SQL (GS_Apps)
    AZURE_SQL_SERVER: z.string().min(1),
    AZURE_SQL_DATABASE: z.string().min(1).default('GS_Apps'),
    SQL_USE_MANAGED_IDENTITY: z.string().optional(),
    AZURE_SQL_USER: z.string().optional(),
    AZURE_SQL_PASSWORD: z.string().optional(),
    // Analytics BD — optionnel Phase 1, obligatoire Phase 2
    // Phase 1 : non défini → même serveur que AZURE_SQL_SERVER
    // Phase 2 : pointer vers Azure SQL dédié analytics
    AZURE_SQL_ANL_SERVER: z.string().min(1).optional(),
    AZURE_SQL_ANL_DATABASE: z.string().min(1).optional().default('GS_Apps'),
    // Multi-pool ADR-036 Q4 — Phase 1 fallback vers AZURE_SQL_*
    // Phase 2 BD : pointer vers 3 BDs en Elastic Pool (ops / warehouse / audit)
    AZURE_SQL_OPS_SERVER: z.string().min(1).optional(),
    AZURE_SQL_OPS_DATABASE: z.string().min(1).optional(),
    AZURE_SQL_WAREHOUSE_SERVER: z.string().min(1).optional(),
    AZURE_SQL_WAREHOUSE_DATABASE: z.string().min(1).optional(),
    AZURE_SQL_AUDIT_SERVER: z.string().min(1).optional(),
    AZURE_SQL_AUDIT_DATABASE: z.string().min(1).optional(),
    // Azure AD workforce — optionnel. Requis par les apps web (MSAL/JWT workforce :
    // admin/inventaire/fit/hub/mcp), MAIS legitimement absent des backends sans auth
    // workforce — ex. api-pwa-auth (tenant External ID gsterrain, pas le workforce) et
    // api-pwa-sync. Ces backends consomment @gs/db (getServerConfig) sans AD ni storage ;
    // les forcer = dummies semantiquement faux (ADR-044, cleanliness). Les consommateurs
    // (getConfig / middlewares auth) les exigent au point d'usage, pas au boot.
    AZURE_AD_CLIENT_ID: z.string().uuid().optional(),
    AZURE_AD_TENANT_ID: z.string().uuid().optional(),
    AZURE_AD_REDIRECT_URI: z.string().url().optional(),
    // Azure Blob Storage — optionnel (requis seulement pour les apps avec upload).
    // Absent des backends sans storage (api-pwa-auth/api-pwa-sync) : pas de secret etale.
    // ADR-057 : Managed Identity -> seul le nom de compte suffit (URL blob derivee).
    AZURE_STORAGE_ACCOUNT: z.string().min(1).optional(),
    // DEPRECATED (ADR-057) : Shared Key. Toleree en transition (plus lue par @gs/storage).
    // A retirer du KV/App Settings apres le flip allowSharedKeyAccess=false (David, §STOP).
    AZURE_STORAGE_CONNECTION_STRING: z.string().min(1).optional(),
    AZURE_STORAGE_CONTAINER: z.string().min(1).default('gs-uploads'),
    // Observability — App Insights connection string (ADR-041 Phase 2).
    // Optionnel : vide en dev local => télémétrie serveur NO-OP silencieux.
    // Phase 2 = App Setting plain string ; Phase 3 = Key Vault reference (ADR-044).
    APPLICATIONINSIGHTS_CONNECTION_STRING: z.string().optional(),
    // SOFI — optionnel (requis uniquement pour l'ETL GS-Analytics)
    SOFI_SQL_SERVER: z.string().min(1).optional(),
    SOFI_SQL_DATABASE: z.string().min(1).optional().default('Signal_Location_XPA'),
    SOFI_SQL_USER: z.string().min(1).optional(),
    SOFI_SQL_PASSWORD: z.string().min(1).optional(),
    // Sélecteur de contexte A3 (ACCESS-CONTROL-STANDARD §5) — secret HMAC du cookie
    // signé qui porte le company_id de session choisi par un admin corporate.
    // OPTIONNEL (n'impacte pas la validation all-or-nothing des backends sans A3) :
    // absent => setContext répond 503 CONTEXT_UNAVAILABLE et le middleware ne
    // honore aucun cookie (fail-closed vers home). >= 16 chars quand présent.
    // App Setting Azure requis pour ACTIVER A3 en prod (prérequis déploiement).
    GS_SESSION_CONTEXT_SECRET: z.string().min(16).optional(),
    // Mailer ACS (@gs/mailer, FRAG R) — capability-aware : defauts absents = envoi
    // desactive (le service repond MAILER_NOT_CONFIGURED, jamais un crash). Deux
    // modes d'auth : GS_ACS_ENDPOINT (Managed Identity, mode cible) OU
    // GS_ACS_CONNECTION_STRING (fallback Key Vault). GS_MAIL_SENDER = adresse NUE
    // (DoNotReply@<domaine>.azurecomm.net). GS_MAIL_MAX_PER_MINUTE borne le throttle.
    GS_ACS_ENDPOINT: z.string().url().optional(),
    GS_ACS_CONNECTION_STRING: z.string().min(1).optional(),
    GS_MAIL_SENDER: z.string().min(1).optional(),
    GS_MAIL_MAX_PER_MINUTE: z.coerce.number().int().positive().optional(),
    // Throttle memoire des routes publiques de reponse au sondage gest
    // (PULSE-SUBMIT P-PS-3) — requetes/minute par IP. Absent => defaut 60.
    GS_PULSE_SUBMIT_MAX_PER_MINUTE: z.coerce.number().int().positive().optional(),
    /** Mode declenchement ETL admin : inprocess (defaut) ou queue (ADR-061 D2, STOP-4). */
    ETL_TRIGGER_MODE: z.enum(['queue', 'inprocess']).default('inprocess'),
    /** Compte Storage de la queue etl-sync-trigger (requis si ETL_TRIGGER_MODE=queue). */
    ETL_QUEUE_ACCOUNT: z.string().min(1).optional(),
    ETL_QUEUE_NAME: z.string().min(1).optional().default('etl-sync-trigger'),
    NODE_ENV: z.enum(['development', 'production', 'test']).default('development'),
})
    .superRefine((data, ctx) => {
    const mi = data.SQL_USE_MANAGED_IDENTITY === 'true';
    if (mi)
        return;
    if (!data.AZURE_SQL_USER?.trim()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Required',
            path: ['AZURE_SQL_USER'],
        });
    }
    if (!data.AZURE_SQL_PASSWORD?.trim()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Required',
            path: ['AZURE_SQL_PASSWORD'],
        });
    }
});
// ─── Schéma ETL-only (SOFI requis) ───────────────────────────
// Utilisé par l'ETL GS-Analytics — valide que SOFI est configuré
const sofiEnvSchema = z.object({
    SOFI_SQL_SERVER: z.string().min(1, 'SOFI_SQL_SERVER manquant — vérifier .env'),
    SOFI_SQL_DATABASE: z.string().min(1).default('Signal_Location_XPA'),
    SOFI_SQL_USER: z.string().min(1, 'SOFI_SQL_USER manquant — vérifier .env'),
    SOFI_SQL_PASSWORD: z.string().min(1, 'SOFI_SQL_PASSWORD manquant — vérifier .env'),
});
const uuidOrEmpty = z.union([z.string().uuid(), z.literal('')]);
const optionalUrl = z.preprocess((v) => (v === '' || v === undefined ? undefined : v), z.string().url().optional());
// ─── Schéma client (Vite) ────────────────────────
/** Schéma client Vite — exporté pour `@gs/config/browser` uniquement */
export const clientEnvSchema = z.object({
    VITE_AZURE_AD_CLIENT_ID: uuidOrEmpty.default(''),
    VITE_AZURE_AD_TENANT_ID: uuidOrEmpty.default(''),
    VITE_AZURE_AD_REDIRECT_URI: optionalUrl,
    VITE_API_URL: z.preprocess((v) => (typeof v === 'string' && v.trim() !== '' ? v.trim() : 'http://localhost:3001'), z.string().url()),
});
// Export du schéma RÉEL (pas une copie) — consommé par le test de contrat IaC
// packages/standards/src/iac-contract.test.ts (ADR-065 : le module bicep gs-app.bicep
// doit poser des App Settings que CE schéma accepte — enum NODE_ENV, AZURE_SQL_*).
export { serverEnvSchema };
let _serverConfig = null;
function formatZodIssues(err) {
    return err.issues.map((e) => `${e.path.join('.') || '(root)'}: ${e.message}`).join('; ');
}
/**
 * Valide `process.env` pour les backends Node.
 * À appeler au démarrage du serveur Express.
 */
export function getServerConfig() {
    if (_serverConfig)
        return _serverConfig;
    const result = serverEnvSchema.safeParse(process.env);
    if (!result.success) {
        throw new Error(`[gs/config] Variables d'environnement invalides : ${formatZodIssues(result.error)}`);
    }
    const d = result.data;
    _serverConfig = {
        ...d,
        AZURE_SQL_USER: d.AZURE_SQL_USER ?? '',
        AZURE_SQL_PASSWORD: d.AZURE_SQL_PASSWORD ?? '',
    };
    return _serverConfig;
}
/**
 * Retourne la configuration SOFI validée.
 * À appeler uniquement depuis l'ETL (@gs/etl, apps/etl-worker).
 * Lance une erreur explicite si SOFI_SQL_* ne sont pas dans le .env.
 */
export function getSofiConfig() {
    const result = sofiEnvSchema.safeParse(process.env);
    if (!result.success) {
        throw new Error(`[gs/config] SOFI non configuré : ${formatZodIssues(result.error)}\n` +
            'Récupérer SOFI_SQL_PASSWORD dans Azure App Service → gs-admin → Configuration.');
    }
    return {
        server: result.data.SOFI_SQL_SERVER,
        database: result.data.SOFI_SQL_DATABASE,
        user: result.data.SOFI_SQL_USER,
        password: result.data.SOFI_SQL_PASSWORD,
        port: 1433,
        options: { encrypt: false, trustServerCertificate: true },
    };
}
// ─── Tuning ETL (MASTER-ETL-PAGINATION-KEYSET 2026-07-12) ────────────────────
// Schéma étroit avec défauts (pattern getSofiConfig) : ne bloque JAMAIS un
// démarrage — une valeur absente prend le défaut, une valeur invalide est
// signalée en warn et retombe sur le défaut.
const ETL_RUNNER_TIMEOUT_MIN_DEFAULT = 18;
const SOFI_REQUEST_TIMEOUT_MS_DEFAULT = 300_000;
const etlTuningSchema = z.object({
    /** Watchdog du pipeline analytics complet (runSofiETL), en minutes. */
    ETL_RUNNER_TIMEOUT_MIN: z.coerce.number().int().positive().default(ETL_RUNNER_TIMEOUT_MIN_DEFAULT),
    /** requestTimeout du driver mssql vers SOFI (GS-SQL01), en millisecondes. */
    SOFI_REQUEST_TIMEOUT_MS: z.coerce.number().int().positive().default(SOFI_REQUEST_TIMEOUT_MS_DEFAULT),
});
/**
 * Tuning des timeouts ETL — consommé par @gs/etl (runners/sofi.ts, sofiConnection.ts).
 * Le watchdog 18 min en dur avait tué le 1er full reload BonLocation post-reset
 * watermark ([BUG-ETL-BONLOCATION-OFFSET-TIMEOUT]) : rendre le plafond pilotable
 * par env sans redéploiement. Défauts INCHANGÉS (18 min / 300 000 ms).
 */
export function getEtlTuningConfig() {
    const result = etlTuningSchema.safeParse(process.env);
    if (!result.success) {
        console.warn(`[gs/config] Tuning ETL invalide (${formatZodIssues(result.error)}) — ` +
            `défauts appliqués : ${ETL_RUNNER_TIMEOUT_MIN_DEFAULT} min / ${SOFI_REQUEST_TIMEOUT_MS_DEFAULT} ms`);
        return {
            runnerTimeoutMin: ETL_RUNNER_TIMEOUT_MIN_DEFAULT,
            sofiRequestTimeoutMs: SOFI_REQUEST_TIMEOUT_MS_DEFAULT,
        };
    }
    return {
        runnerTimeoutMin: result.data.ETL_RUNNER_TIMEOUT_MIN,
        sofiRequestTimeoutMs: result.data.SOFI_REQUEST_TIMEOUT_MS,
    };
}
/**
 * Configuration agrégée pour Node (Azure SQL, storage, etc.).
 * Sans `import.meta` — sûr pour bundle CJS (esbuild serveur).
 * Pour le navigateur : `import { getConfig } from '@gs/config/browser'`.
 */
/**
 * Config effective pour la couche analytics Azure SQL (ADR-019).
 * Phase 1 : identique à la BD opérationnelle si les variables ANL ne sont pas définies.
 */
export function getAnalyticsDbConfig() {
    const s = getServerConfig();
    return {
        server: s.AZURE_SQL_ANL_SERVER ?? s.AZURE_SQL_SERVER,
        database: s.AZURE_SQL_ANL_DATABASE ?? s.AZURE_SQL_DATABASE,
        user: s.AZURE_SQL_USER ?? '',
        password: s.AZURE_SQL_PASSWORD ?? '',
        port: 1433,
        options: { encrypt: true, trustServerCertificate: false },
    };
}
function buildPoolConfig(serverOverride, databaseOverride) {
    const s = getServerConfig();
    return {
        server: serverOverride ?? s.AZURE_SQL_SERVER,
        database: databaseOverride ?? s.AZURE_SQL_DATABASE,
        user: s.AZURE_SQL_USER ?? '',
        password: s.AZURE_SQL_PASSWORD ?? '',
        port: 1433,
        options: { encrypt: true, trustServerCertificate: false },
    };
}
export function getOpsSqlConfig() {
    const s = getServerConfig();
    return buildPoolConfig(s.AZURE_SQL_OPS_SERVER, s.AZURE_SQL_OPS_DATABASE);
}
export function getWarehouseSqlConfig() {
    const s = getServerConfig();
    return buildPoolConfig(s.AZURE_SQL_WAREHOUSE_SERVER, s.AZURE_SQL_WAREHOUSE_DATABASE);
}
export function getAuditSqlConfig() {
    const s = getServerConfig();
    return buildPoolConfig(s.AZURE_SQL_AUDIT_SERVER, s.AZURE_SQL_AUDIT_DATABASE);
}
export function getPoolSqlConfig(name) {
    switch (name) {
        case 'ops': return getOpsSqlConfig();
        case 'warehouse': return getWarehouseSqlConfig();
        case 'audit': return getAuditSqlConfig();
    }
}
/**
 * Détection robuste de la prod Azure (cf ADR-014).
 *
 * `NODE_ENV` seul est insuffisant : sur Azure App Service Linux, la variable
 * peut être absente ou valoir `development` malgré un déploiement prod, ce qui
 * a déjà causé un bug 2026-03 (`isDev = NODE_ENV === 'development'` exposait
 * les stack traces en clair). On combine donc avec `WEBSITE_SITE_NAME` qui est
 * toujours défini par App Service.
 *
 * Utilisation par défaut côté serveur :
 * - `errorHandler` (expose stack en dev only)
 * - features de debug (CORS open, healthz verbose, etc.)
 */
export function isProductionAzure() {
    return process.env.NODE_ENV === 'production' || !!process.env.WEBSITE_SITE_NAME;
}
/** Inverse de `isProductionAzure()` — sécurité par défaut : tout sauf prod = dev. */
export function isDevelopment() {
    return !isProductionAzure();
}
export function getConfig() {
    const s = getServerConfig();
    return {
        sql: {
            server: s.AZURE_SQL_SERVER,
            database: s.AZURE_SQL_DATABASE,
            user: s.AZURE_SQL_USER ?? '',
            password: s.AZURE_SQL_PASSWORD ?? '',
            port: 1433,
            options: { encrypt: true, trustServerCertificate: false },
        },
        auth: {
            // Les 3 champs AD/storage sont optionnels au niveau schema (backends sans
            // workforce AD/storage). getConfig() est appele par les apps qui en ont besoin
            // (elles les definissent) -> fallback '' sans casser le type (cf AZURE_SQL_USER).
            clientId: s.AZURE_AD_CLIENT_ID ?? '',
            tenantId: s.AZURE_AD_TENANT_ID ?? '',
            redirectUri: s.AZURE_AD_REDIRECT_URI ?? 'http://localhost:5173',
            scopes: ['User.Read', 'openid', 'profile', 'email'],
        },
        storage: {
            // ADR-057 : Managed Identity — `account` suffit (URL blob derivee). La
            // connection string Shared Key reste exposee mais DEPRECATED (non lue).
            account: s.AZURE_STORAGE_ACCOUNT ?? '',
            connectionString: s.AZURE_STORAGE_CONNECTION_STRING ?? '',
            containerName: s.AZURE_STORAGE_CONTAINER,
        },
        apiUrl: process.env.VITE_API_URL ?? 'http://localhost:3001',
        nodeEnv: s.NODE_ENV,
    };
}
/** Liste des 11 régions GS */
export const GS_REGIONS = [
    'Montréal',
    'Sainte-Catherine',
    'Saint-Jérôme',
    'Joliette',
    'Trois-Rivières',
    'Drummondville',
    'Sherbrooke',
    'Rouyn-Noranda',
    'Jonquière',
    'Gatineau',
    'Lévis',
];
/** Catégories d'articles GS */
export const GS_CATEGORIES = ['Signalisation', 'EPI', 'Lettrage'];
/**
 * UUID GS Quebec — tenant unique Phase 1 mono-tenant (HVB-01, ADR-036 amendement).
 * À retirer après Phase 2 (multi-tenant opérationnel).
 *
 * NE PAS UTILISER pour résoudre le tenant en runtime — utiliser `req.companyId`
 * depuis le middleware `injectCompanyId`. Cette constante sert UNIQUEMENT dans les
 * `readClaims` Phase 1 qui retournent `{ companyId: GS_PHASE1_COMPANY_ID }` pour les
 * apps sans claim Azure AD `companyId` (à venir Phase 2).
 */
export const GS_PHASE1_COMPANY_ID = '00000000-0000-0000-0000-000000000001';
//# sourceMappingURL=index.js.map